import * as stylex from '@stylexjs/stylex'
import { type ComponentPropsWithoutRef, type ForwardedRef, type ReactNode } from 'react'
import { Text, type TextProps } from '../Text/Text'
import { colors } from '../tokens/colors.stylex'

const baseStyles = stylex.create({
  root: {
    gap: 2,
    alignItems: 'center',
    color: colors.grey600,
    display: 'flex',
    position: 'relative',
  },
  error: {
    color: colors.red600,
  },
  link: {
    color: colors.blue600,
  },
  color: (color: string) => ({ color }),
  clickable: {
    cursor: 'pointer',
  },
  text: {
    width: '100%',
  },
  floating: {
    position: 'absolute',
    top: '100%',
  },
})

export interface HelpTextProps extends ComponentPropsWithoutRef<'div'> {
  /**
   * 輔助文字內容
   */
  children?: ReactNode
  /**
   * 文字對齊方式
   */
  align?: TextProps['align']
  /**
   * 是否不換行（超出省略）
   */
  noWrap?: TextProps['noWrap']
  /**
   * 自訂文字顏色
   */
  color?: string
  /**
   * 前方圖示
   */
  icon?: ReactNode
  /**
   * 是否顯示錯誤狀態
   */
  error?: boolean
  /**
   * 是否顯示為連結樣式
   */
  link?: boolean
  /**
   * 是否以絕對定位方式渲染（脫離文件流）
   */
  floating?: boolean
  /**
   * 根元素自訂樣式
   */
  rootStyle?: stylex.StyleXStyles
  /**
   * 點擊事件回呼
   */
  onClick?: () => void
  /**
   * 根元素的 ref
   */
  rootRef?: ForwardedRef<HTMLDivElement>
}

/**
 * HelpText 輔助說明文字元件，常用於表單欄位下方的提示或錯誤訊息。
 * 支援錯誤狀態、連結樣式及自訂圖示。
 *
 * @example
 * ```tsx
 * <HelpText error>此欄位為必填</HelpText>
 * ```
 */
export function HelpText(props: HelpTextProps) {
  const { align, icon, error, color, link, noWrap, onClick, rootStyle, rootRef, children, floating, ...rest } = props

  const clickable = !!onClick || link

  return (
    <div
      ref={rootRef}
      onClick={onClick}
      {...stylex.props(
        baseStyles.root,
        clickable && baseStyles.clickable,
        link && baseStyles.link,
        error && baseStyles.error,
        !!color && baseStyles.color(color),
        rootStyle,
      )}
      {...rest}
    >
      {icon}
      <Text variant="caption" align={align} noWrap={noWrap} rootStyle={[baseStyles.text, floating && baseStyles.floating]}>
        {children}
      </Text>
    </div>
  )
}
